/**
 * 外部接口配置（OCR / 验真 / 企查查 / T+ / 钉钉）
 *
 * 这一页直接决定「系统会不会真的去调外部接口、会不会产生费用、会不会写外部系统」，
 * 所以每张卡片都把三类风险写在明面上：
 *   1. 真实模式 = 真实调用 + 真实计费（沙箱模式不产生外部调用与费用）；
 *   2. 进项锁定是**不可逆的写操作**，默认关闭，开启需要二次确认并留下理由；
 *   3. 企查查经营范围不匹配只提示、不拦截（默认关闭强制拦截）。
 *
 * 另外，T+ 的配置里明确写着「只推业务数据、不生成凭证」，防止实施人员误以为这里能配凭证生成。
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Row,
  Select,
  Slider,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ApiOutlined,
  DeleteOutlined,
  DollarOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  ThunderboltOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  DEFAULT_ROLES,
  PermissionCode,
  PROVIDER_LABELS,
  ProviderCode,
  ProviderMode,
  type ProviderConfig,
} from '@ilc/shared';
import { metaApi, tenantApi } from '@/api/endpoints';
import { mockProviderConfigList, mockTenantMetricData } from '@/mocks';
import { useResource } from '@/hooks/useResource';
import { useAuthStore } from '@/stores/auth';
import { PageHeader } from '@/components/PageHeader';
import { ProviderModeTag } from '@/components/ProviderModeTag';
import { ConfirmDanger } from '@/components/ConfirmDanger';
import { performAction } from '@/utils/performAction';
import { feedback } from '@/utils/feedback';

/*
 * 模式文案与配色已收敛到 `@ilc/shared` 的 PROVIDER_MODE_LABELS/DESCRIPTIONS
 * 与 `@/components/ProviderModeTag`。
 * 从前这里有第三份手写副本，新增枚举值时会静默漏掉、把未知值显示成「人工」。
 */

/** 各接口的专用配置项说明 */
const PROVIDER_SCOPES: Record<ProviderCode, string> = {
  OCR: '发票票面识别：上传后自动识别，逐字段给出置信度，低置信度转人工修正',
  TAX_VERIFY: '发票验真：核对票面与税务侧记录，可配置是否执行进项锁定（不可逆写操作）',
  QCC: '企查查主体校验：销方存续状态、经营范围匹配、风险信息；默认只提示不拦截',
  TPLUS: '畅捷通 T+：把已通过审核的业务数据推入待办业务池；凭证由财务在 T+ 端生成',
  DINGTALK: '钉钉：待办提醒与风险事件推送，需为接收人配置钉钉 userId',
  SMS: '短信：仅用于验证码与高优先级告警，按条计费',
  CONTRACT_EXTRACT:
    '合同解析：PDF/Word 本地解析（零成本）；扫描件走阿里云全文识别，该服务需单独开通',
  INVOICE_HANDWRITING:
    '发票复印件手写识别：提取票面上的手写签字/日期/批注；走阿里云手写体识别，' +
    '与发票识别是两个独立产品需分别开通，且**没有沙箱与本地回落**——未启用时附件仍会正常保存，只是不提取手写内容',
};

/**
 * 进项锁定的说明。
 *
 * 【这段文案曾经是一句不成立的承诺】旧版写「开启后，锁定的发票在税务端将无法继续
 * 勾选抵扣」，暗示"打开开关就会去锁"。事实是：**没有任何代码会执行锁定**——
 * 当前通道（阿里云验真）只提供查询核验、连 lockInput 方法都没有（现已如实返回
 * `CHANNEL_UNSUPPORTED`），编排层也没有调用方，`tax_check` 的三个锁定列只读不写。
 * 用户打开开关后什么都不会发生，而界面上没有任何地方说得清这一点。
 *
 * 现在改成如实说明：开关记录的是**意图与安全闸门**，真正执行还需要两个前提
 * （换支持勾选认证的通道 + 接通执行入口），在做不到之前界面不会暗示"已生效"。
 */
const INPUT_LOCK_HELP =
  '开启后不会立即执行锁定：本平台当前没有接通税务侧的锁定执行链路，' +
  '且当前验真通道（阿里云）只做查询核验、不具备锁定能力。本开关记录的是"本租户要求锁定"的意图，' +
  '作为后续接入的准入条件与安全闸门。真正执行需要两个前提：改用支持「进项发票勾选认证」的通道' +
  '（乐企或第三方服务商），并接通执行入口。在两者就绪前，发票详情里的进项锁定状态会一直是「未发起」或「当前通道不支持」。';

interface ProviderFormValues {
  enabled: boolean;
  remark?: string;
  qccBlockOnAbnormal?: boolean;
  qccScopeThreshold?: number;
  taxEnableInputLock?: boolean;
  taxSpecialReleaseRoleCode?: string | null;
  tplusAccountSet?: string | null;
  fieldMapping?: Array<{ key?: string; value?: string }>;
  dingtalkAgentId?: string | null;
}

export default function Providers(): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const canWrite = can(PermissionCode.PROVIDER_CONFIG_WRITE);
  const [form] = Form.useForm<ProviderFormValues>();
  const [editing, setEditing] = useState<ProviderConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [lockConfirmOpen, setLockConfirmOpen] = useState(false);

  const providers = useResource<ProviderConfig[]>(() => tenantApi.providers(), {
    fallback: () => mockProviderConfigList(),
  });
  const metrics = useResource(() => metaApi.tenantMetrics(), {
    fallback: () => mockTenantMetricData(),
  });

  const list = providers.data ?? [];
  const quotaMap = useMemo(() => {
    const map = new Map<string, { used: number; limit: number }>();
    for (const item of metrics.data?.quotaUsage ?? []) {
      map.set(item.provider, { used: item.used, limit: item.limit });
    }
    return map;
  }, [metrics.data]);

  const stats = useMemo(() => {
    const enabled = list.filter((p) => p.enabled).length;
    /* 统计"实际走真实通道"的条数，而不是数 provider_config.mode——
     * 那一列是死配置，数它会显示"真实调用模式 3 个"而实际一个都没跑真实通道。 */
    const real = list.filter((p) => p.effective?.mode === ProviderMode.REAL).length;
    let used = 0;
    let limit = 0;
    for (const item of quotaMap.values()) {
      used += item.used;
      limit += item.limit;
    }
    return { enabled, real, used, limit };
  }, [list, quotaMap]);

  const openEdit = (config: ProviderConfig): void => {
    setEditing(config);
  };

  /**
   * 表单初值。**不要改回在 openEdit 里调 setFieldsValue**。
   * 弹窗带 `destroyOnHidden`，关闭时 `<Form>` 被卸载，此刻的同步赋值会被丢弃，
   * 重新挂载时又按 initialValues（空）初始化——表现为"点配置，标题对、里面全空"。
   * 详见 `LegalWorkbench.tsx` 的说明。
   */
  const formInitialValues = useMemo<ProviderFormValues>(
    () => ({
      enabled: editing?.enabled ?? false,
      remark: editing?.remark ?? '',
      qccBlockOnAbnormal: editing?.qccBlockOnAbnormal ?? false,
      qccScopeThreshold: editing?.qccScopeThreshold ?? 0,
      taxEnableInputLock: editing?.taxEnableInputLock ?? false,
      taxSpecialReleaseRoleCode: editing?.taxSpecialReleaseRoleCode ?? '',
      tplusAccountSet: editing?.tplusAccountSet ?? '',
      fieldMapping: Object.entries(editing?.tplusFieldMapping ?? {}).map(([key, value]) => ({
        key,
        value,
      })),
      dingtalkAgentId: editing?.dingtalkAgentId ?? '',
    }),
    [editing],
  );

  /** 组装提交体：只提交「与当前编辑的接口相关」的字段，避免误改其它接口的配置 */
  const buildBody = (values: ProviderFormValues, config: ProviderConfig, lockReason?: string) => {
    /* 不提交 mode：通道由部署配置（.env 的 *_DRIVER）决定，服务端也已不再接受
     * 这个字段。提交它只会让界面显得"改了"，实际毫无作用。 */
    const base = {
      enabled: Boolean(values.enabled),
      remark: values.remark ? String(values.remark) : null,
    };
    if (config.provider === ProviderCode.QCC) {
      return {
        ...base,
        qccBlockOnAbnormal: Boolean(values.qccBlockOnAbnormal),
        qccScopeThreshold: Number(values.qccScopeThreshold ?? 60),
      };
    }
    if (config.provider === ProviderCode.TAX_VERIFY) {
      return {
        ...base,
        taxEnableInputLock: Boolean(values.taxEnableInputLock),
        taxSpecialReleaseRoleCode: values.taxSpecialReleaseRoleCode ?? null,
        // 仅在"开启进项锁定"的二次确认里带上；后端会把它写进审计日志的 detail
        ...(lockReason ? { taxInputLockReason: lockReason } : {}),
      };
    }
    if (config.provider === ProviderCode.TPLUS) {
      const mapping: Record<string, string> = {};
      for (const item of values.fieldMapping ?? []) {
        const key = (item.key ?? '').trim();
        const value = (item.value ?? '').trim();
        if (key) mapping[key] = value;
      }
      return {
        ...base,
        tplusAccountSet: values.tplusAccountSet ? String(values.tplusAccountSet) : null,
        tplusFieldMapping: Object.keys(mapping).length > 0 ? mapping : null,
      };
    }
    if (config.provider === ProviderCode.DINGTALK) {
      return { ...base, dingtalkAgentId: values.dingtalkAgentId ? String(values.dingtalkAgentId) : null };
    }
    return base;
  };

  /**
   * 保存接口配置。
   *
   * `lockReason` 是"开启进项锁定"二次确认里填的理由——它会**随请求发给后端**
   * 并写进平台审计日志。从前这里只把它拼进 toast 文案（`note`），
   * 弹窗上却写着"将写入平台审计日志"，等于又一处说了做不到的话。
   */
  const persist = async (
    values: ProviderFormValues,
    config: ProviderConfig,
    lockReason?: string,
  ): Promise<void> => {
    setSaving(true);
    const result = await performAction({
      action: () => tenantApi.updateProvider(config.provider, buildBody(values, config, lockReason)),
      successMessage: `${PROVIDER_LABELS[config.provider]}配置已保存`,
      offline: () => undefined,
      offlineMessage: '后端接口配置接口尚未就绪（/api/tenant/providers 返回 404），本次保存仅在本地演示，未写入数据库',
    });
    setSaving(false);
    if (result.ok) {
      setEditing(null);
      setLockConfirmOpen(false);
      providers.reload();
    }
  };

  const submit = async (): Promise<void> => {
    if (!editing) return;
    const values = await form.validateFields();
    const turningLockOn = Boolean(values.taxEnableInputLock) && !editing.taxEnableInputLock;
    if (turningLockOn) {
      // 不可逆写操作：先让管理员确认并填写理由，再真正提交
      setLockConfirmOpen(true);
      return;
    }
    await persist(values, editing);
  };

  const toggleEnabled = async (config: ProviderConfig, next: boolean): Promise<void> => {
    if (config.provider === ProviderCode.TAX_VERIFY && config.taxEnableInputLock) {
      feedback.warning('验真接口当前启用了进项锁定，请先进入配置关闭进项锁定再停用接口，避免锁定状态失控');
      return;
    }
    await performAction({
      action: () => tenantApi.updateProvider(config.provider, { enabled: next }),
      successMessage: `${PROVIDER_LABELS[config.provider]}已${next ? '启用' : '停用'}`,
      offline: () => undefined,
      offlineMessage: '后端接口配置接口尚未就绪，本次变更仅在本地演示',
    });
    providers.reload();
  };

  return (
    <div className="ilc-page">
      <PageHeader
        title="外部接口配置"
        description="配置 OCR、发票验真、企查查、畅捷通 T+、钉钉等外部接口的开关、运行模式与参数。配置按租户隔离。"
        tags={<Tag icon={<ApiOutlined />} color="geekblue">影响真实调用与费用</Tag>}
        extra={
          <Button icon={<ReloadOutlined />} onClick={providers.reload}>
            刷新
          </Button>
        }
      />

      {!canWrite ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="当前账号为只读"
          description="您没有 provider:config:write 权限，可以查看接口配置与用量，但不能修改开关、模式与参数。"
        />
      ) : null}

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 12 }}
        message="改配置前请先确认三件事"
        description={
          <ul style={{ margin: '4px 0 0', paddingInlineStart: 18, lineHeight: 1.9 }}>
            <li>
              <strong>通道由部署配置决定，本页改不了</strong>：走真实还是沙箱，取决于服务端
              <code>.env</code> 里的 <code>OCR_DRIVER</code> / <code>TAX_VERIFY_DRIVER</code> /{' '}
              <code>QCC_DRIVER</code>，改完需重启服务。本页「运行模式」一列展示的是
              <strong>实际生效</strong>的通道，不是可编辑项。
            </li>
            <li>
              <strong>真实通道会真实调用并计费</strong>：真实调用会计入配额；沙箱与本地解析不产生费用。
            </li>
            <li>
              <strong>进项锁定是不可逆写操作</strong>：开启后会在税务侧写入锁定标记，本平台无法自动撤销。
            </li>
          </ul>
        }
      />

      <Row gutter={[16, 16]} style={{ marginBottom: 12 }}>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="已启用接口" value={stats.enabled} suffix={`/ ${list.length}`} />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title="实际走真实通道"
              value={stats.real}
              suffix={`/ ${list.length}`}
              valueStyle={{ color: stats.real > 0 ? '#d46b08' : undefined }}
              prefix={stats.real > 0 ? <ThunderboltOutlined /> : undefined}
            />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic title="本月调用合计" value={stats.used} suffix={`次 / ${stats.limit || '-'}`} />
          </Card>
        </Col>
        <Col xs={12} lg={6}>
          <Card size="small">
            <Statistic
              title="超额风险接口"
              value={[...quotaMap.values()].filter((q) => q.limit > 0 && q.used / q.limit >= 0.9).length}
              suffix="个"
              valueStyle={{
                color:
                  [...quotaMap.values()].filter((q) => q.limit > 0 && q.used / q.limit >= 0.9).length > 0
                    ? '#cf1322'
                    : undefined,
              }}
            />
          </Card>
        </Col>
      </Row>

      <Table<ProviderConfig>
        rowKey="id"
        size="small"
        bordered
        loading={providers.loading}
        dataSource={list}
        locale={{ emptyText: '接口配置接口未就绪或暂无数据' }}
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={[
          {
            title: '接口',
            dataIndex: 'provider',
            width: 150,
            fixed: 'left',
            render: (v: ProviderCode, record) => (
              <Space direction="vertical" size={0}>
                <Space size={4}>
                  <Typography.Text strong>{PROVIDER_LABELS[v]}</Typography.Text>
                </Space>
                <Typography.Text type="secondary" style={{ fontSize: 11 }} className="ilc-mono">
                  {v}
                </Typography.Text>
                {record.provider === ProviderCode.TAX_VERIFY && record.taxEnableInputLock ? (
                  <Tag color="red" icon={<WarningOutlined />}>
                    进项锁定已开启
                  </Tag>
                ) : null}
              </Space>
            ),
          },
          {
            title: '启用',
            dataIndex: 'enabled',
            width: 90,
            render: (v: boolean, record) => (
              // 说明：这个开关目前**不影响运行时**——接口启停由部署配置（.env）决定。
              // 加 tooltip 说清楚，避免让人以为拨一下就能切换通道。
              <Tooltip title="接口启停由服务端 .env 配置决定，本开关仅作登记">
                <Switch
                  size="small"
                  checked={v}
                  disabled={!canWrite}
                  checkedChildren="开"
                  unCheckedChildren="关"
                  onChange={(next) => void toggleEnabled(record, next)}
                />
              </Tooltip>
            ),
          },
          {
            title: '运行模式（实际生效）',
            key: 'effectiveMode',
            width: 220,
            render: (_v, record) => {
              const eff = record.effective;
              if (!eff) return <Typography.Text type="secondary">未知</Typography.Text>;
              return (
                <Space direction="vertical" size={2}>
                  <ProviderModeTag mode={eff.mode} impl={eff.impl} />
                  {!eff.healthy && eff.detail ? (
                    <Typography.Text type="danger" style={{ fontSize: 12 }}>
                      {eff.detail}
                    </Typography.Text>
                  ) : null}
                </Space>
              );
            },
          },
          {
            title: '本月用量 / 配额',
            key: 'quota',
            width: 220,
            render: (_v, record) => {
              const quota = quotaMap.get(record.provider);
              if (!quota) return <Typography.Text type="secondary">未配置配额</Typography.Text>;
              const percent = quota.limit > 0 ? Math.min(100, Math.round((quota.used / quota.limit) * 100)) : 0;
              return (
                <Space direction="vertical" size={0} style={{ width: '100%' }}>
                  <Progress
                    percent={percent}
                    size="small"
                    status={percent >= 100 ? 'exception' : percent >= 90 ? 'active' : 'normal'}
                    strokeColor={percent >= 90 ? '#cf1322' : undefined}
                  />
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    <span className="ilc-num">{(quota.used ?? 0).toLocaleString('zh-CN')}</span> /{' '}
                    <span className="ilc-num">{(quota.limit ?? 0).toLocaleString('zh-CN')}</span>
                    {percent >= 100 ? ' · 配额已用尽，调用会被平台拦截' : ''}
                  </Typography.Text>
                </Space>
              );
            },
          },
          {
            title: '关键配置',
            key: 'summary',
            width: 320,
            render: (_v, record) => {
              const items: string[] = [];
              if (record.provider === ProviderCode.QCC) {
                items.push(
                  `经营范围匹配阈值 ${record.qccScopeThreshold}%`,
                  record.qccBlockOnAbnormal ? '不匹配时强制拦截' : '不匹配时仅提示（默认）',
                );
              }
              if (record.provider === ProviderCode.TAX_VERIFY) {
                items.push(
                  record.taxEnableInputLock ? '进项锁定：开启（不可逆）' : '进项锁定：关闭（默认）',
                  `特例放行审批角色：${
                    record.taxSpecialReleaseRoleCode
                      ? DEFAULT_ROLES.find((r) => r.code === record.taxSpecialReleaseRoleCode)?.name ??
                        record.taxSpecialReleaseRoleCode
                      : '未指定'
                  }`,
                );
              }
              if (record.provider === ProviderCode.TPLUS) {
                items.push(
                  `账套：${record.tplusAccountSet ?? '未配置'}`,
                  `字段映射：${Object.keys(record.tplusFieldMapping ?? {}).length} 项`,
                  '仅推送业务数据，不生成凭证',
                );
              }
              if (record.provider === ProviderCode.DINGTALK) {
                items.push(`应用 AgentId：${record.dingtalkAgentId ?? '未配置'}`);
              }
              if (record.provider === ProviderCode.OCR) {
                items.push('识别字段模板见「租户配置」页');
              }
              return (
                <Space direction="vertical" size={2}>
                  {items.map((text) => (
                    <Typography.Text key={text} type="secondary" style={{ fontSize: 12 }}>
                      {text}
                    </Typography.Text>
                  ))}
                  {record.remark ? (
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      备注：{record.remark}
                    </Typography.Text>
                  ) : null}
                </Space>
              );
            },
          },
          {
            title: '操作',
            key: 'action',
            width: 110,
            fixed: 'right',
            render: (_v, record) => (
              <Button size="small" type="link" disabled={!canWrite} onClick={() => openEdit(record)}>
                配置
              </Button>
            ),
          },
        ]}
      />

      <Card size="small" title="配置影响与费用提示" style={{ marginTop: 16 }}>
        <Descriptions column={1} size="small">
          {(Object.keys(PROVIDER_SCOPES) as ProviderCode[]).map((code) => (
            <Descriptions.Item key={code} label={PROVIDER_LABELS[code]}>
              {PROVIDER_SCOPES[code]}
            </Descriptions.Item>
          ))}
        </Descriptions>
        <Divider style={{ margin: '12px 0' }} />
        <Space direction="vertical" size={4}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            <DollarOutlined /> 费用按实际计费调用统计，沙箱与人工兜底模式不计费；
            「人工兜底」适用于外部接口故障期间，此时相关校验项会标记为「外部校验暂不可用」。
          </Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            所有接口调用（含沙箱）都会写入调用日志并保留原始报文，可在「调用日志」页查看。
          </Typography.Text>
        </Space>
      </Card>

      <Modal
        open={editing !== null}
        title={`配置 ${editing ? PROVIDER_LABELS[editing.provider] : ''}`}
        onCancel={() => setEditing(null)}
        onOk={() => void submit()}
        confirmLoading={saving}
        width={680}
        destroyOnHidden
        okText="保存配置"
        okButtonProps={{ icon: <SaveOutlined />, disabled: !canWrite }}
      >
        {editing ? (
          <Form<ProviderFormValues>
              /* key 取接口代码：切换编辑对象时整体重建，不留上一个接口的残留值 */
              key={editing?.provider ?? 'none'}
              form={form}
              layout="vertical"
              preserve={false}
              initialValues={formInitialValues}
            >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={PROVIDER_SCOPES[editing.provider]}
            />
            <Row gutter={12}>
              <Col span={12}>
                {/* 只读展示：通道选择属于部署级配置（.env 的 *_DRIVER），
                    改这里不会生效，从前那个可编辑 Select 只会误导人。 */}
                <Form.Item
                  label="运行模式（实际生效，只读）"
                  extra={
                    editing.effective?.detail ??
                    '通道由服务端 .env 的 *_DRIVER 决定，修改需重启服务'
                  }
                >
                  {editing.effective ? (
                    <ProviderModeTag mode={editing.effective.mode} impl={editing.effective.impl} />
                  ) : (
                    <Typography.Text type="secondary">未知</Typography.Text>
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="enabled"
                  label="启用该接口"
                  valuePropName="checked"
                  extra="本页开关仅作登记，实际启停由部署配置决定"
                >
                  <Switch disabled={!canWrite} />
                </Form.Item>
              </Col>

              {editing.provider === ProviderCode.QCC ? (
                <>
                  <Col span={12}>
                    <Form.Item
                      name="qccScopeThreshold"
                      label="经营范围匹配置信度阈值"
                      extra="低于阈值判定为「经营范围不匹配」，默认 60%"
                    >
                      <Slider min={0} max={100} marks={{ 0: '0', 60: '60', 90: '90', 100: '100' }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="qccBlockOnAbnormal"
                      label="经营异常时强制拦截"
                      valuePropName="checked"
                      extra="默认关闭：经营异常/不匹配只做中风险提示，操作仍可继续并留痕"
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Alert
                      type="warning"
                      showIcon
                      message="开启强制拦截会改变业务流程"
                      description="开启后，企查查判定异常或经营范围不匹配的发票将无法继续流转 T+，只能退回经办人或走特例放行。多数租户不需要开启——经营范围不匹配属于中风险，业务上允许继续并留痕。"
                    />
                  </Col>
                </>
              ) : null}

              {editing.provider === ProviderCode.TAX_VERIFY ? (
                <>
                  <Col span={24}>
                    <Form.Item
                      name="taxEnableInputLock"
                      label={
                        <Space>
                          <span>执行进项锁定</span>
                          <Tag color="red" icon={<WarningOutlined />}>
                            不可逆写操作
                          </Tag>
                        </Space>
                      }
                      valuePropName="checked"
                      extra={INPUT_LOCK_HELP}
                    >
                      <Switch />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Alert
                      type="warning"
                      showIcon
                      message="本开关目前只记录意图：平台不会执行任何锁定动作"
                      description={
                        <Space direction="vertical" size={4}>
                          <span>
                            开启需要二次确认并填写理由，理由会写入平台审计日志（分类：配置变更）——
                            留痕是为了将来接入真正可锁定的通道时，能查清"当初是谁、依据什么把它打开的"。
                          </span>
                          <span>
                            验真本身只做查询，无论开关如何都不会向税务侧写数据；因此这个开关
                            <b>不会</b>造成任何不可逆后果，也<b>不会</b>让发票真的被锁定。
                          </span>
                        </Space>
                      }
                    />
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="taxSpecialReleaseRoleCode"
                      label="特例放行审批角色"
                      extra="高风险发票走特例放行时，由该角色审批并留存专项审批依据"
                    >
                      <Select
                        allowClear
                        placeholder="选择角色"
                        options={DEFAULT_ROLES.map((r) => ({ value: r.code as string, label: r.name }))}
                      />
                    </Form.Item>
                  </Col>
                </>
              ) : null}

              {editing.provider === ProviderCode.TPLUS ? (
                <>
                  <Col span={12}>
                    <Form.Item
                      name="tplusAccountSet"
                      label="T+ 账套"
                      rules={[{ required: true, message: '请填写 T+ 账套名称' }]}
                      extra="需与 T+ 端账套名称完全一致，否则推送会失败"
                    >
                      <Input placeholder="如 演示科技-默认账套" />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Alert
                      type="warning"
                      showIcon
                      message="本平台不生成会计凭证"
                      description="该接口只把发票业务数据推入 T+ 的待办业务池。会计科目与记账凭证由财务在 T+ 端选择生成，本平台仅同步业务数据。"
                    />
                  </Col>
                  <Col span={24}>
                    <Divider orientation="left" plain>
                      字段映射（本平台字段 → T+ 单据字段）
                    </Divider>
                    <Form.List name="fieldMapping">
                      {(fields, { add, remove }) => (
                        <Space direction="vertical" size={8} style={{ width: '100%' }}>
                          {fields.map((field) => (
                            <Space key={field.key} align="baseline" wrap>
                              <Form.Item
                                name={[field.name, 'key']}
                                rules={[{ required: true, message: '请输入本平台字段' }]}
                                style={{ marginBottom: 0 }}
                              >
                                <Input className="ilc-mono" placeholder="invoiceNo" style={{ width: 200 }} />
                              </Form.Item>
                              <span style={{ color: 'rgba(0,0,0,0.35)' }}>→</span>
                              <Form.Item name={[field.name, 'value']} style={{ marginBottom: 0 }}>
                                <Input placeholder="发票号码" style={{ width: 200 }} />
                              </Form.Item>
                              <Button
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => remove(field.name)}
                              />
                            </Space>
                          ))}
                          <Button size="small" icon={<PlusOutlined />} onClick={() => add({ key: '', value: '' })}>
                            添加字段映射
                          </Button>
                        </Space>
                      )}
                    </Form.List>
                  </Col>
                </>
              ) : null}

              {editing.provider === ProviderCode.DINGTALK ? (
                <Col span={12}>
                  <Form.Item
                    name="dingtalkAgentId"
                    label="应用 AgentId"
                    extra="钉钉应用的 AgentId，用户需绑定钉钉 userId 才能收到待办"
                  >
                    <Input className="ilc-mono" placeholder="dingtalk-agent-xxxx" />
                  </Form.Item>
                </Col>
              ) : null}

              <Col span={24}>
                <Form.Item name="remark" label="备注">
                  <Input.TextArea rows={2} placeholder="记录配置来源、审批单号等，便于事后追溯" />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        ) : null}
      </Modal>

      {/**
       * 二次确认弹窗。
       *
       * 【措辞改了：不再宣称"会在税务侧写入"】旧文案（标题带"不可逆写操作"、
       * 勾选项写"开启后会在税务侧写入锁定标记"）描述的是一套**并不存在**的执行链路。
       * 如实说明后仍保留这段确认流程，因为理由要落进平台审计日志。
       * 另一个老问题一并修掉：**填的理由从前根本没发给后端**——
       * `persist` 只把它拼进 toast 文案，审计记录里没有理由，
       * 而弹窗上白纸黑字写着"将写入平台审计日志"。
       */}
      <ConfirmDanger
        open={lockConfirmOpen}
        title="开启进项锁定策略声明"
        level="normal"
        confirmText="确认开启"
        acknowledgeText="我已知晓：本开关只记录意图，平台当前不会向税务侧写入任何锁定动作"
        description={
          <Space direction="vertical" size={4}>
            <span>{INPUT_LOCK_HELP}</span>
            <span>请记录内部审批依据（例如：审批单号、审批人），它会写入平台审计日志备查。</span>
          </Space>
        }
        reasonLabel="开启理由（必填，不少于 15 字，将写入平台审计日志）"
        minReasonLength={15}
        reasonPresets={[
          '经财务负责人审批（审批单号见备注），待通道接入后本租户需锁定进项防止重复抵扣',
          '税务稽查要求对相关期间进项做锁定处理，已完成内部审批并留存依据',
        ]}
        loading={saving}
        onConfirm={async (reason) => {
          if (!editing) return;
          const values = await form.validateFields();
          await persist(values, editing, reason);
        }}
        onCancel={() => setLockConfirmOpen(false)}
      />

      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, marginBottom: 0 }}>
        提示：费用按「分」为单位记录，页面按元展示，口径与发票金额一致；接口调用的原始报文请在「调用日志」页查看，
        日志不做软删除，按保留策略归档。
      </Typography.Paragraph>
    </div>
  );
}
